import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

/* Iconos */
import { FaFacebookF, FaInstagram, FaWhatsapp } from "react-icons/fa";
import { FiMail, FiPhone, FiMapPin } from "react-icons/fi";
import { SiWaze } from "react-icons/si";

/* Logo Deko (ajusta la ruta si cambió en tu proyecto) */
import logoFooter from "../../assets/images/LogoBlanco.svg";

/* Estilos */
import "./Footer.css";

/* ========= Helpers ========= */
const toTelHref = (phone) => `tel:${(phone || "").replace(/\s+/g, "")}`;

// Genera enlace de Waze por nombre de sede (no requiere coordenadas)
const wazeFrom = (loc) =>
  loc?.waze
    ? loc.waze
    : `https://waze.com/ul?q=${encodeURIComponent(`${loc?.title || ""}, Costa Rica`)}&navigate=yes`;

/* ========= Data Deko ========= */
/* Edita los nombres si lo deseas. Puedes añadir 'waze' explícito por sede si tienes el URL. */
const dekoLocations = [
  { id: "palmares", title: "Palmares" },
  { id: "alajuela", title: "Alajuela" },
  { id: "san-jose", title: "San José" },
  { id: "escazu", title: "Escazú" },
  { id: "tamarindo", title: "Tamarindo" },
  { id: "uvita", title: "Uvita" },
  { id: "jaco", title: "Jaco" },
  { id: "pinares", title: "Pinares" },
];

/* ========= Component ========= */
const Footer = () => {
  const { t } = useTranslation();

  // URLs sociales (vienen de i18n si existen; si no, usan fallback seguro)
  const facebookUrl  = t("footer.social.facebookUrl", "https://www.facebook.com/");
  const instagramUrl = t("footer.social.instagramUrl", "https://www.instagram.com/");
  const whatsappUrl  = t("footer.social.whatsappUrl", "https://wa.me/50661686664");

  // Contacto (usa claves de contacto globales si las tienes, con fallback)
  const email   = t("contact.info.email.value", t("footer.contact.email", "rsantamaria@gruposantamaria.cr"));
  const phone   = t("contact.info.phone.value", t("footer.contact.phone", "+506 6168 6664"));
  const country = t("footer.contact.country", "Costa Rica");

  return (
    <footer className="footer" role="contentinfo">
      <div className="footer__container">
        {/* TOP */}
        <div className="footer__top">
          {/* Marca */}
          <div className="footer__brand">
            <img
              src={logoFooter}
              alt={t("footer.brandAlt", "Deko by Santamaría")}
              className="footer__logo"
            />
            <p className="footer__tagline">
              {t("footer.tagline", "Transformando espacios con elegancia y durabilidad")}
            </p>

            <div className="footer__socials" aria-label={t("footer.aria.socials", "Redes sociales")}>
              <a
                href={facebookUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                className="social-icon"
                title="Facebook"
              >
                <FaFacebookF aria-hidden="true" />
              </a>
              <a
                href={instagramUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="social-icon"
                title="Instagram"
              >
                <FaInstagram aria-hidden="true" />
              </a>
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="WhatsApp"
                className="social-icon"
                title="WhatsApp"
              >
                <FaWhatsapp aria-hidden="true" />
              </a>
            </div>
          </div>

          {/* Grid de columnas */}
          <div className="footer__grid">
            {/* Productos / Marcas */}
            <nav className="footer__col" aria-label={t("footer.aria.productsNav", "Enlaces de productos")}>
              <h4 className="footer__title">{t("footer.columns.products", "Productos")}</h4>
              <ul className="footer__list">
                {/* Si tienes rutas internas para categorías Deko, cámbialas aquí */}
                <li>
                  <Link to="https://deko.gruposantamaria.cr/" className="footer__link">
                    {t("footer.products.items.deko", "Deko")}
                  </Link>
                </li>
                <li>
                  <Link to="https://rollerup.gruposantamaria.cr/" className="footer__link">
                    {t("footer.products.items.rollerUp", "Roller Up")}
                  </Link>
                </li>
                <li>
                  <Link to="https://dreamon.gruposantamaria.cr/" className="footer__link">
                    {t("footer.products.items.dreamOn", "Dream On")}
                  </Link>
                </li>
                <li>
                  <Link to="https://sofa.gruposantamaria.cr/" className="footer__link">
                    {t("footer.products.items.sofaFactory", "The Sofa Factory")}
                  </Link>
                </li>
                <li>
                  <Link to="https://textiles.gruposantamaria.cr/" className="footer__link">
                    {t("footer.products.items.textiles", "Textiles Santamaría")}
                  </Link>
                </li>
              </ul>
            </nav>

            {/* Showrooms con enlace a Waze */}
            <div className="footer__col" aria-label={t("footer.aria.showroomsRegion", "Ubicaciones de showrooms")}>
              <h4 className="footer__title">{t("footer.columns.showrooms", "Showrooms")}</h4>
              <ul className="footer__list">
                {dekoLocations.map((loc) => (
                  <li key={loc.id} className="footer__showroom-item">
                    <span className="footer__showroom-name">{loc.title}</span>
                    <a
                      className="waze-link"
                      href={wazeFrom(loc)}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={t("footer.showrooms.wazeTitle", { name: loc.title, defaultValue: `Navegar a ${loc.title} en Waze` })}
                      aria-label={t("footer.showrooms.wazeAria", { name: loc.title, defaultValue: `Abrir Waze para ${loc.title}` })}
                    >
                      <SiWaze aria-hidden="true" />
                      <span>{t("footer.showrooms.waze", "Cómo llegar")}</span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contacto */}
            <address
              className="footer__col footer__contact"
              aria-label={t("footer.aria.contactRegion", "Información de contacto")}
            >
              <h4 className="footer__title">{t("footer.columns.contact", "Contacto")}</h4>
              <ul className="footer__list">
                <li className="footer__contact-item">
                  <span className="icon"><FiMail aria-hidden="true" /></span>
                  <a href={`mailto:${email}`} className="footer__link">{email}</a>
                </li>
                <li className="footer__contact-item">
                  <span className="icon"><FiPhone aria-hidden="true" /></span>
                  <a href={toTelHref(phone)} className="footer__link">{phone}</a>
                </li>
                <li className="footer__contact-item">
                  <span className="icon"><FiMapPin aria-hidden="true" /></span>
                  <span>{country}</span>
                </li>
              </ul>
            </address>
          </div>
        </div>

        {/* BOTTOM */}
        <div className="footer__bottom">
          <p className="footer__copyright">
            {t("footer.bottom.copyright", {
              year: new Date().getFullYear(),
              defaultValue: `© ${new Date().getFullYear()} Grupo Santamaría. Todos los derechos reservados.`,
            })}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
